import {Location, History} from "history";
import {ReactNode, Suspense, useRef, useSyncExternalStore} from "react";
import {Root} from "../../cdx-models/Root";
import {KeyPressContextProvider, springConfigs, useReveal} from "@cdx/common";
import {Col, css, DSSpinner, SimplePortal} from "@cdx/ds";
import {animated} from "react-spring";
import FocusLock from "react-focus-lock";
import {focusLockKeyGroupContext} from "../../components/Modals";
import {VisionBoard} from "../../cdx-models/VisionBoard";
import {colorThemes} from "@cdx/ds/css/themes/color-overwrites.css";
import {FullScreenNodeContext} from "../../lib/hooks/useFullscreen";
import VisionBoardCanvas from "./VisionBoardCanvas";

const QUERY_PARAM = "visionBoard";

type RegisterVisionBoardProps = {
  root: Root;
  location: Location;
  history: History;
};

const stopPropagation = (e: Event) => {
  e.stopPropagation();
};
const preventPropagationHandlers = {
  onClick: stopPropagation,
  onMouseDown: stopPropagation,
};

const Container = ({children, revealProps}: {children: ReactNode; revealProps: any}) => {
  const focusGroupName = "visionBoard";
  const nodeRef = useRef<HTMLDivElement>(null);

  return (
    <SimplePortal>
      <Col
        position="fixed"
        style={{top: 52}}
        left="0"
        width="100%"
        height="100%"
        zIndex={2}
        overflow="hidden"
        {...preventPropagationHandlers}
      >
        <FocusLock group={focusGroupName} returnFocus autoFocus={false}>
          <focusLockKeyGroupContext.Provider value={focusGroupName as any}>
            <KeyPressContextProvider>
              <animated.div
                className={css(
                  {
                    display: "flex",
                    flexDir: "column",
                    position: "absolute",
                    top: "0",
                    left: "0",
                    width: "100%",
                    height: "100%",
                    bg: "foreground",
                  },
                  colorThemes.gray550
                )}
                style={{
                  opacity: revealProps.value,
                  pointerEvents: revealProps.value.to((val: number) =>
                    val > 0.7 ? "initial" : "none"
                  ),
                }}
                ref={nodeRef}
              >
                <Suspense fallback={<DSSpinner size={20} />}>
                  <FullScreenNodeContext.Provider value={nodeRef}>
                    {children}
                  </FullScreenNodeContext.Provider>
                </Suspense>
              </animated.div>
            </KeyPressContextProvider>
          </focusLockKeyGroupContext.Provider>
        </FocusLock>
      </Col>
    </SimplePortal>
  );
};

export const useVisionBoardSeqFromLocation = (location: Location, history: History) => {
  return useSyncExternalStore(history.listen, () => {
    const query = new URLSearchParams(location.search);
    return Number(query.get(QUERY_PARAM)) ?? null;
  });
};

const RegisterVisionBoard = (props: RegisterVisionBoardProps) => {
  const {root, location, history} = props;
  const seq = useVisionBoardSeqFromLocation(location, history);
  const visionBoard = seq
    ? root.account.$meta.first("visionBoards", {accountSeq: seq, $order: "id"})
    : null;
  const revealFn = useReveal(visionBoard, {config: springConfigs.quickNoBounce} as any);
  const handleClose = () => {
    const qs = new URLSearchParams(location.search);
    qs.delete(QUERY_PARAM);
    history.push({...location, search: qs.toString()});
  };
  return revealFn((styles: any, board: VisionBoard) => (
    <Container revealProps={styles}>
      <VisionBoardCanvas
        board={board}
        root={root}
        onClose={handleClose}
        location={location}
        history={history}
      />
    </Container>
  ));
};

export default RegisterVisionBoard;
